/*
  CommandK — global search palette. Keyboard shortcut ⌘K (Mac) / Ctrl+K
  (everywhere else). Searches clients, enquiries, tasks and documents,
  debounces at 200ms, and navigates on Enter or click.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader2, Search, User, HelpCircle, ClipboardCheck, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

const KIND_META = {
  client: { icon: User, label: "Client", accent: "text-indigo-700" },
  enquiry: { icon: HelpCircle, label: "Enquiry", accent: "text-amber-700" },
  task: { icon: ClipboardCheck, label: "Task", accent: "text-emerald-700" },
  document: { icon: FileText, label: "Document", accent: "text-sky-700" },
};

export default function CommandK() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Global shortcut: ⌘K / Ctrl+K opens; Esc closes.
  useEffect(() => {
    function onKey(e) {
      const isK = (e.key || "").toLowerCase() === "k";
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Focus + reset on open.
  useEffect(() => {
    if (!open) return;
    setQ(""); setResults([]); setCursor(0);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  // Debounced search.
  const search = useCallback(async (query) => {
    const trimmed = (query || "").trim();
    if (!trimmed) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get("/global-search", { params: { q: trimmed } });
      setResults(r.data?.results || []);
      setCursor(0);
    } catch {
      setResults([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 200);
    return () => clearTimeout(debounceRef.current);
  }, [q, search]);

  function pick(r) {
    if (!r?.href) return;
    setOpen(false);
    navigate(r.href);
  }

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(results.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(results[cursor]);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-xl w-[92vw] p-0 gap-0"
        data-testid="command-k-dialog"
      >
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <DialogDescription className="sr-only">
          Search across clients, enquiries, tasks, and documents. Use arrow keys to navigate, Enter to open.
        </DialogDescription>
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 text-zinc-400" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search clients, enquiries, tasks, documents…"
            className="border-0 shadow-none focus-visible:ring-0 h-10 px-0 text-sm"
            data-testid="command-k-input"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
          <kbd className="hidden sm:inline text-2xs px-1.5 py-0.5 border border-zinc-200 rounded text-zinc-500 bg-zinc-50">
            Esc
          </kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {q && !loading && results.length === 0 && (
            <div className="text-sm text-zinc-600 py-8 text-center"
                 data-testid="command-k-empty">
              Nothing matches "{q}".
            </div>
          )}
          {!q && (
            <div className="text-xs text-zinc-600 py-6 text-center space-y-1">
              <div>Type to search across the whole app.</div>
              <div className="text-2xs opacity-80">
                Clients · Enquiries · Tasks · Documents
              </div>
            </div>
          )}
          {results.length > 0 && (
            <ul className="py-1">
              {results.map((r, i) => {
                const meta = KIND_META[r.kind] || KIND_META.client;
                const Icon = meta.icon;
                const active = i === cursor;
                return (
                  <li
                    key={`${r.kind}:${r.href}:${i}`}
                    className={cn(
                      "px-3 py-2 flex items-center gap-2 cursor-pointer transition",
                      active ? "bg-indigo-50" : "hover:bg-zinc-50",
                    )}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => pick(r)}
                    data-testid={`command-k-result-${i}`}
                  >
                    <Icon className={cn("h-4 w-4 flex-shrink-0", meta.accent)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate font-medium">{r.label}</div>
                      {r.sublabel && (
                        <div className="text-2xs truncate text-zinc-500">{r.sublabel}</div>
                      )}
                    </div>
                    <span className="text-2xs uppercase text-zinc-400 tracking-wide">
                      {meta.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="border-t px-3 py-1.5 text-2xs text-zinc-600 flex items-center gap-3">
          <span><kbd className="border rounded px-1">↑</kbd><kbd className="border rounded px-1">↓</kbd> navigate</span>
          <span><kbd className="border rounded px-1">Enter</kbd> open</span>
          <span className="ml-auto"><kbd className="border rounded px-1">⌘K</kbd> to reopen</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
